import { FUNERAL_SIDO, FUNERAL_SIGUNGU } from "goi_common";
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import parlors_discount from "@/constants/parlors_discount_detail.json";
import ParlorRegionContainer from "@/containers/Parlor/ParlorRegionContainer/ParlorRegionContainer";
import { ParlorDetailProps } from "@/types/parlor";
import { replaceSiGunToBlank } from "@/utils/replaceSigunToBlank";
import { getParlorInfoByRegionCode } from "@/utils/getRegionParlorInfo";
import { getRegionParlorStats, RegionParlorStats } from "@/utils/getRegionParlorStats";

import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const sidoList = Array.from(FUNERAL_SIDO.values());
  const sidoPath = sidoList.map((sido) => {
    return {
      params: {
        code: sido,
      },
    };
  });

  const sigunguPaths = Array.from(FUNERAL_SIDO)
    .map((sido) => {
      const sigungus = FUNERAL_SIGUNGU.get(sido[0]);
      if (sigungus) {
        return Array.from(sigungus.values()).map((sigungu) => {
          return {
            params: {
              code: `${sido[1]}-${sigungu}`,
            },
          };
        });
      }
    })
    .filter((i) => i != null);

  return {
    paths: sidoPath.concat(sigunguPaths?.flat() as Array<any>),
    fallback: false,
  };
};

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (value === searchValue) return key;
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.code as string;

  if (code.includes("-")) {
    const [sido, sigungu] = code.split("-");
    const sidoNumber = getByValue(FUNERAL_SIDO, sido);
    const sigunguNumber = getByValue(FUNERAL_SIGUNGU.get(sidoNumber as number) as any, sigungu);

    const { regionParlors, hasSigunguParlor } = getParlorInfoByRegionCode(
      sidoNumber as number,
      sigunguNumber as number,
    );

    const sortedRegionParlors = regionParlors.sort((p) =>
      parlors_discount.map((dp) => dp.parlor_id).includes(Number(p.id)) ? -1 : 1,
    );

    const regionStats = getRegionParlorStats(sortedRegionParlors as unknown as ParlorDetailProps[]);

    const sigunguKeyword = replaceSiGunToBlank(sigungu);

    const representativeNames = sortedRegionParlors
      .slice(0, 2)
      .map((p) => p.companyname)
      .filter(Boolean);
    const representativeText =
      representativeNames.length > 0 ? `${representativeNames.join(", ")} 등 ` : "";

    // 시군구에 시설이 없으면 regionStats는 시도 fallback 값이라 "N곳"을 시군구에 붙이면 거짓이 된다.
    // 이 경우 카운트를 아예 빼고 지역명만 남긴다 ("0곳"으로 찍히거나 시도 숫자를 시군구에 붙이는 것보다 나음)
    const countSuffix = hasSigunguParlor ? ` ${regionStats.parlorCount}곳` : "";

    const description = `${sido} ${sigungu} 지역 장례식장${countSuffix}의 시설정보, 빈소 비용, 위치를 비교하세요. ${representativeText}공설·사설 구분과 주차, 편의시설 정보까지 한눈에 확인할 수 있습니다.`;

    const title = `${sigungu} 장례식장${countSuffix} | ${sido} 장례식장 정보`;

    return {
      props: {
        regionParlors,
        regionStats,
        hasSigunguParlor,
        sidoName: sido,
        sido: sidoNumber,
        sigunguName: sigungu,
        sigungu: sigunguNumber,
        isSigungu: true,
        pageDescription: description,
        pageKeywords: `${sigunguKeyword} 장례식장, ${sigunguKeyword} 장례식장 비용, ${sido} ${sigunguKeyword} 장례식장`,
        pageOgTitle: title,
        pageOgDescription: description,
        pageOgImage:
          regionParlors[0]?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      },
    };
  } else {
    const sidoNumber = getByValue(FUNERAL_SIDO, code);

    const { regionParlors, hasSigunguParlor } = getParlorInfoByRegionCode(sidoNumber as number);

    const sortedRegionParlors = regionParlors.sort((p) =>
      parlors_discount.map((dp) => dp.parlor_id).includes(Number(p.id)) ? -1 : 1,
    );

    const regionStats = getRegionParlorStats(sortedRegionParlors as unknown as ParlorDetailProps[]);

    // 시도 페이지는 기존 검색 성과가 안정적이라 main 원본 메타 유지 (개선 실험 반경은 군구·서비스 한정)
    const 무빈소장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();
    const 가족장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();
    const 일반장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();

    const description = `${code}장례식장 상세 비용, 가장 저렴한 ${code}장례식장 비용 정보, 주변 장례식장과 비교하기. ${code}장례식장 무빈소장례 비용:${
      무빈소장 || 980000
    }원, ${code}장례식장 가족장 비용:${가족장 || 3700000}원, ${code}장례식장 일반장 비용:${
      일반장 || 480000
    }원. 상조, 장례식장, 장지 비용 한번에 알아보기`;

    const title = `${code}장례식장 추천, 비용, 무빈소장례, 가족장, 일반장 비용 비교하기 - [장례의 모든 것, 고이장례연구소]`;

    return {
      props: {
        regionParlors: sortedRegionParlors,
        regionStats,
        hasSigunguParlor,
        sidoName: code,
        sido: getByValue(FUNERAL_SIDO, code),
        isSigungu: false,
        pageDescription: description,
        pageKeywords: `${code} 장례식장 추천, ${code}장례식장 비용, ${code}장례식장 평균 예상 비용, ${code}장례식장 무빈소장례, ${code}장례식장 가족장, ${code}장례식장 일반장, ${code}장례식장 무빈소장례 비용,  ${code}장례식장 가족장 비용,  ${code}장례식장 일반장 비용`,
        pageOgTitle: title,
        pageOgDescription: description,
        pageOgImage:
          regionParlors[0]?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      },
    };
  }
};

interface ParlorRegionPageProps {
  regionParlors: Array<ParlorDetailProps>;
  regionStats: RegionParlorStats;
  hasSigunguParlor: boolean;
  sido: string;
  sidoName: string;
  sigungu?: string;
  sigunguName?: string;
  isSigungu: boolean;
}

function ParlorRegionPage(props: ParlorRegionPageProps) {
  const {
    sido,
    sidoName,
    sigungu,
    sigunguName,
    isSigungu,
    regionParlors,
    regionStats,
    hasSigunguParlor,
  } = props;

  return (
    <>
      <ParlorRegionContainer
        regionParlors={regionParlors}
        regionStats={regionStats}
        hasSigunguParlor={hasSigunguParlor}
        sido={sido}
        sidoName={sidoName}
        sigungu={sigungu}
        sigunguName={sigunguName}
        isSigungu={isSigungu}
      />
    </>
  );
}

export default ParlorRegionPage;
